import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Pill from '@adsk/bim360-matrix-react-pill';
import IconButton from '@adsk/bim360-matrix-react-icon-button';
import { StatusColorIndicator } from '@adsk/bim360-matrix-react-status-color-indicator';
import SvgIcon from '@adsk/bim360-matrix-react-svg-icon';
import closeIcon from '@adsk/bim360-matrix-react-assets/modal-close.svg';
import OverflowTooltip from '@adsk/bim360-matrix-react-overflow-tooltip';
import Tooltip from '@adsk/bim360-matrix-react-tooltip';
import objectValues from '@adsk/bim360-matrix-react-helpers/es/objectValues';
import { Colors } from '@adsk/bim360-matrix-react-helpers/es/consts';

import './_EntityBlock.scss';

const EntityBlock = ({
  className,
  entityTypeName,
  userFriendlyId,
  deeplinkUrl,
  title,
  statusColor,
  statusText,
  isRenderedMultiline,
  onRemoveClick,
  container,
  onReferenceClick
}) => {
  const tooltipRef = useRef(null);

  const showStatus = statusColor && statusText;
  const onClick = deeplinkUrl
    ? e => {
      e.preventDefault();
      if (!tooltipRef.current || !tooltipRef.current.contains(e.target)) {
        window.open(deeplinkUrl);
      }

      if (onReferenceClick && typeof onReferenceClick === 'function') {
        onReferenceClick();
      }
    }
    : undefined;

  const mainClassName = classNames('EntityBlock__main', {
    'EntityBlock__main--clickable': !!onClick,
    'EntityBlock__main--removable': !!onRemoveClick,
    'EntityBlock__main--multiline': !!isRenderedMultiline
  });
  const textContainerClassName = classNames('EntityBlock__text-container');

  const classes = classNames('EntityBlock', className);
  const textItemClass = 'EntityBlock__text-item';
  const idFieldClasses = classNames('EntityBlock__id', textItemClass);
  const statusClasses = classNames(textItemClass, 'EntityBlock__status-text');
  const titleClasses = classNames('EntityBlock__title', textItemClass);

  return (
    <div className={classes}>
      <div className={mainClassName} onClick={onClick}>
        {isRenderedMultiline ? (
          <>
            {showStatus && <StatusColorIndicator statusColor={statusColor} />}
            <div className={textContainerClassName}>
              <div className={statusClasses}>
                {userFriendlyId && <div className={idFieldClasses}>{userFriendlyId}</div>}
                {statusText}
              </div>
              <div className={titleClasses}>
                <OverflowTooltip
                  container={container}
                  overlayRef={tooltipRef}
                  singleLine={true}
                  autoTipOnEllipsis={true}
                  triggerTarget={Tooltip.TriggerTargets.TOOLTIP}
                  theme={Tooltip.Themes.DARK}
                >
                  {title}
                </OverflowTooltip>
              </div>
            </div>
          </>
        ) : (
          <>
            <div className={textItemClass}>{entityTypeName}</div>
            {showStatus && (
              <Pill color={statusColor} className={textItemClass} label={statusText} />
            )}
            {userFriendlyId && <div className={idFieldClasses}>{userFriendlyId}</div>}
            <div className={titleClasses}>
              <OverflowTooltip
                container={container}
                overlayRef={tooltipRef}
                singleLine={true}
                autoTipOnEllipsis={true}
                triggerTarget={Tooltip.TriggerTargets.TOOLTIP}
                theme={Tooltip.Themes.DARK}
              >
                {title}
              </OverflowTooltip>
            </div>
          </>
        )}
      </div>
      {onRemoveClick && (
        <IconButton
          className="EntityBlock__remove-button"
          onClick={e => {
            e.preventDefault();
            e.stopPropagation();
            onRemoveClick();
          }}
        >
          <SvgIcon width="10px" height="10px">
            {closeIcon}
          </SvgIcon>
        </IconButton>
      )}
    </div>
  );
};

EntityBlock.propTypes = {
  /**
   * Class to add to the component
   */
  className: PropTypes.string,
  /**
   * Entity type name - a user facing text describing entity type.
   */
  entityTypeName: PropTypes.string.isRequired,
  /**
   * The title of the entity.
   */
  title: PropTypes.string.isRequired,
  /**
   * A user friendly ID if exists.
   */
  userFriendlyId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  /**
   * A deeplink for showing the entity in its original feature module.
   */
  deeplinkUrl: PropTypes.string,
  /**
   * The status color of the entity. On of Colors values.
   */
  statusColor: PropTypes.oneOf(objectValues(Colors)),
  /**
   * The status text of the entity.
   */
  statusText: PropTypes.string,
  /**
   * Controls the text rendering of the block, false is inline (default), true is multi-line.
   */
  isRenderedMultiline: PropTypes.bool,
  /**
   * Handler when clicking remove entity button.
   */
  onRemoveClick: PropTypes.func,
  /**
   * A container to attach tooltips to.
   */
  container: PropTypes.object,
  /**
   * Callback fired when a user clicks on the block.
   */
  onReferenceClick: PropTypes.func
};

export default EntityBlock;
